import {classes} from './PaymentAndPlaceOrderStep.st.css';
import {PaymentsWidget} from '@wix/cashier-payments-widget/lazy';
import {
  getCashierMandatoryFieldsOverrides,
  getMandatoryFieldsByCheckout,
} from '../../../../../domain/utils/cashier.utils';
import React, {useEffect} from 'react';
import {useControllerProps} from '../../../Widget/ControllerContext';
import {usePaymentsApi} from '../../../../../common/components/WithPaymentsApi/WithPaymentsApi';
import {StartPayment} from '../../../../../domain/utils/analytics.utils';
import {getPaymentsWidgetBIData} from '../../../../../domain/utils/bi.util';
import {StepState} from '../../../../../types/checkoutApp.types';
import {getCountryFromLocaleDataset} from '../../../../../domain/utils/localeDataset.util';
import {useBillingData} from './WithBillingData';
import {useExperiments} from '@wix/yoshi-flow-editor';
import {MANDATORY_FIELDS_HIDDEN, SPECS} from '../../../../../common/constants';
import {type MFieldName} from '@wix/cashier-payments-widget/src/types/MandatoryFields';

export enum PaymentWidgetWrapperDataHooks {
  root = 'PaymentWidgetWrapperDataHooks.root',
  emptyRoot = 'PaymentWidgetWrapperDataHooks.emptyRoot',
}

export const PaymentWidgetWrapper = ({
  stepState,
  paymentWidgetRef,
}: {
  stepState: StepState;
  paymentWidgetRef?: React.RefObject<HTMLDivElement>;
}) => {
  const {
    paymentStore: {cashierWidgetProps, setCashierMandatoryFields},
    navigationStore: {trackEvent},
    checkoutStore: {cart},
  } = useControllerProps();

  const {setPaymentsApi, setActivePaymentId} = usePaymentsApi();
  const {contactCountry} = useBillingData();
  const {experiments} = useExperiments();
  const makeGetPaymentDetailsIdSync = experiments.enabled(SPECS.MakeGetPaymentDetailsIdSync);

  useEffect(
    () => {
      if (stepState === StepState.OPEN) {
        trackEvent(...StartPayment);
      }
    },
    /* eslint-disable react-hooks/exhaustive-deps */ [stepState]
  );

  const wrapperClass: {[state: string]: string} = {
    [StepState.EMPTY]: classes.paymentEmpty,
    [StepState.OPEN]: classes.paymentOpen,
  };

  /* istanbul ignore if */
  if (!cashierWidgetProps) {
    return <div className={wrapperClass[stepState]} data-hook={PaymentWidgetWrapperDataHooks.emptyRoot}></div>;
  }

  const paymentsWidgetMandatoryProps = makeGetPaymentDetailsIdSync
    ? {
        mandatoryFieldsHidden: MANDATORY_FIELDS_HIDDEN,
      }
    : {
        mandatoryFields: getMandatoryFieldsByCheckout(
          cart,
          contactCountry,
          experiments.enabled(SPECS.UseLocalDatasetDirectlyInCheckout)
        ),
      };

  const handleMandatoryFieldsChanged = (mandatoryFields: MFieldName[]) => {
    if (experiments.enabled(SPECS.SupportDynamicBillingFieldsCheckout)) {
      setCashierMandatoryFields(getCashierMandatoryFieldsOverrides(mandatoryFields));
    }
  };

  const isWidgetHidden = stepState === StepState.EMPTY;

  const {configuration, ...cashierProps} = cashierWidgetProps;
  const country3 = contactCountry
    ? getCountryFromLocaleDataset(contactCountry, experiments.enabled(SPECS.UseLocalDatasetDirectlyInCheckout))?.key
    : undefined;
  return (
    <div className={wrapperClass[stepState]} data-hook={PaymentWidgetWrapperDataHooks.root} ref={paymentWidgetRef}>
      <PaymentsWidget
        {...cashierProps}
        biData={getPaymentsWidgetBIData(cart)}
        configuration={{...configuration, country: country3}}
        isWidgetVisible={!isWidgetHidden}
        isFullWidth={true}
        onApiInit={setPaymentsApi}
        onMandatoryFieldsChanged={handleMandatoryFieldsChanged}
        paymentMethodChanged={setActivePaymentId}
        paymentResult={/* istanbul ignore next */ () => null}
        {...paymentsWidgetMandatoryProps}
      />
    </div>
  );
};
